import React from "react";
import moment from "moment";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import ParticipacaoEmEmpresas from "../../common/query/ParticipacaoEmEmpresas";
import Enderecos from "../../common/query/Enderecos";
import Emails from "../../common/query/Emails";
import Telefones from "../../common/query/Telefones";
import { formatDocument } from "../../../utils/utils";
import { FiUserCheck } from "react-icons/fi";

const buildDurationLabel = (fromMoment, suffix = "") => {
  if (!fromMoment || !fromMoment.isValid()) return null;
  const years = moment().diff(fromMoment, "years");
  const afterYears = fromMoment.clone().add(years, "years");
  const months = moment().diff(afterYears, "months");
  const parts = [];
  if (years) parts.push(`${years} ano${years === 1 ? "" : "s"}`);
  if (months) parts.push(`${months} mês${months === 1 ? "" : "es"}`);
  if (!parts.length) return `menos de 1 mês${suffix ? ` ${suffix}` : ""}`;
  const joined = parts.join(" e ");
  return suffix ? `${joined} ${suffix}` : joined;
};

const LocalizacaoPFQuery = ({ data }) => {
  const cadastro = data?.dadosCadastrais || {};
  const birthMoment = cadastro.dataNascimento
    ? moment(cadastro.dataNascimento, ["DD/MM/YYYY", "YYYY-MM-DD"], true)
    : null;
  const birthLabel =
    birthMoment && birthMoment.isValid()
      ? birthMoment.format("DD/MM/YYYY")
      : null;
  const birthDuration = birthMoment
    ? buildDurationLabel(birthMoment, "")
    : null;

  const summaryCards = [
    {
      label: "Nome da mãe",
      value: cadastro.nomeMae || "Não informado",
      hint: "Parente",
    },
    {
      label: "Origem CPF",
      value: cadastro.regiaoCPF || "Não informado",
      hint: "Região",
    },
    {
      label: "Sexo",
      value: cadastro.sexo || "Não informado",
      hint: "Gênero",
    },
    {
      label: "Telefone",
      value: cadastro.telefone || "Não informado",
      hint: "Contato",
    },
  ];

  return (
    <div className="space-y-10 mt-8">
      <div>
        <TitleStripe>
          <div className="flex items-center gap-2">
            <FiUserCheck className="text-blue-700" />{" "}
            <span>Dados Cadastrais</span>
          </div>
        </TitleStripe>
        <div className="mt-4 rounded-[24px] border border-gray-200 bg-white/80 p-4 shadow-sm text-xs">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                Nome
              </p>
              <p className="text-xl font-semibold text-gray-900">
                {cadastro.nome || "Indisponível"}
              </p>
            </div>
          </div>

          <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
              Situação: {cadastro.situacao || "Desconhecida"}
            </span>
            {birthLabel && (
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                Nasc: {birthLabel}
              </span>
            )}
            {birthDuration && (
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                {birthDuration}
              </span>
            )}
          </div>

          <div className="mt-3 grid gap-2 grid-cols-2 sm:grid-cols-2 md:grid-cols-4">
            {summaryCards.map((item) => (
              <div
                key={item.label}
                className="rounded-xl border border-gray-100 bg-slate-50 px-3 py-2"
              >
                <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                  {item.label}
                </p>
                <p className="text-sm font-semibold text-gray-900">
                  {item.value}
                </p>
                <p className="text-[10px] text-gray-500">{item.hint}</p>
              </div>
            ))}
          </div>

          <div className="mt-3 rounded-xl border border-gray-100 bg-white px-3 py-2 text-[10px]">
            <p className="font-semibold text-gray-700">Endereço</p>
            <p className="text-sm font-medium text-gray-900">
              {cadastro.endereco || "—"}
              {cadastro.numero && `, ${cadastro.numero}`}
            </p>
            <p className="text-[10px] text-gray-500">
              {cadastro.bairro || "—"} · {cadastro.cidade || "—"}{" "}
              {cadastro.uf ? `/ ${cadastro.uf}` : ""}
            </p>
          </div>
        </div>
      </div>

      {data?.participacaoEmEmpresas && (
        <div>
          <TitleStripe>🏢 Participação em Empresas</TitleStripe>
          {data.participacaoEmEmpresas.length > 0 ? (
            <ParticipacaoEmEmpresas empresas={data.participacaoEmEmpresas} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.enderecos && (
        <div>
          <TitleStripe>📍 Endereços</TitleStripe>
          {data.enderecos.length > 0 ? (
            <Enderecos enderecos={data.enderecos} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.emails && (
        <div>
          <TitleStripe>📧 E-mails</TitleStripe>
          {data.emails.length > 0 ? (
            <Emails emails={data.emails} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.telefones && (
        <div>
          <TitleStripe>📞 Telefones</TitleStripe>
          {data.telefones.length > 0 ? (
            <Telefones telefones={data.telefones} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}
    </div>
  );
};

export default LocalizacaoPFQuery;
